/**
 * useWipModules — Hook léger qui charge la liste des modules en développement.
 * Permet aux composants (sidebar, pages) de griser ou afficher un message
 * pour les modules temporairement désactivés par le SuperAdmin.
 *
 * Politique : les SuperAdmin ne sont jamais "bridés" — ils peuvent continuer
 * à tester les modules WIP normalement (pour finir le développement).
 */
import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';

let _cache = {
  wipModuleIds: [],
  messages: {},
  isSuperAdmin: false,
  loadedAt: 0,
};
const _listeners = new Set();
const TTL_MS = 60 * 1000; // 1 minute

function _emit() {
  for (const fn of _listeners) {
    try { fn({ ..._cache }); } catch { /* noop */ }
  }
}

async function _fetch(force = false) {
  if (!force && Date.now() - _cache.loadedAt < TTL_MS && _cache.loadedAt > 0) {
    return _cache;
  }
  try {
    const { data } = await api.get('/modules/wip-public');
    _cache = {
      wipModuleIds: Array.isArray(data?.wipModuleIds) ? data.wipModuleIds : [],
      messages: data?.messages || {},
      isSuperAdmin: !!data?.isSuperAdmin,
      loadedAt: Date.now(),
    };
    _emit();
  } catch {
    // Silencieux : si l'API renvoie 401 ou 5xx, on garde le cache existant
  }
  return _cache;
}

export function useWipModules() {
  const [state, setState] = useState(_cache);

  useEffect(() => {
    let mounted = true;
    _fetch().then((s) => { if (mounted) setState({ ...s }); });
    const listener = (s) => { if (mounted) setState({ ...s }); };
    _listeners.add(listener);
    return () => { mounted = false; _listeners.delete(listener); };
  }, []);

  const refresh = useCallback(() => _fetch(true), []);

  /** Renvoie true si l'utilisateur courant doit voir le module comme "WIP" (grisé).
   *  Les SuperAdmin ne sont jamais bridés. */
  const isModuleBlocked = useCallback(
    (moduleId) => {
      if (state.isSuperAdmin) return false;
      return state.wipModuleIds.includes(moduleId);
    },
    [state],
  );

  /** Message custom configuré par le SuperAdmin pour ce module. */
  const getModuleMessage = useCallback(
    (moduleId) => state.messages?.[moduleId] || null,
    [state],
  );

  return {
    wipModuleIds: state.wipModuleIds,
    isSuperAdmin: state.isSuperAdmin,
    isModuleBlocked,
    getModuleMessage,
    refresh,
  };
}

/** Force le rafraîchissement du cache (à appeler après un toggle SuperAdmin). */
export function invalidateWipCache() {
  return _fetch(true);
}
